import { Field } from '@atlaskit/form';
import get from 'lodash/get';
import Select, { OptionProps, ValueType } from '@atlaskit/select';
import React from 'react';
import { generatedTypesNew, Order, OrderLineItem, Promotion, Maybe } from '@css-xp/graphtypes';
import capitalize from 'lodash/capitalize';
import lowerCase from 'lodash/lowerCase';
import {
  getDescriptor,
  getOrderLineItemStatusAndType,
  getReadablePromotionTitle,
  getReadablePromotionValue,
} from '../../entitlement-utils';
import { FormattedDate } from '../../formatted';

export interface OrderSelectLineItemV2 {
  value: string;
  order: Order;
}

const getPromotionDetailsMessage = (promotions: OrderLineItem['promotions']) => {
  return promotions?.map((promotion: Maybe<Promotion>, index: number) => {
    return (
      <div key={index}>
        <p>Promotion: {getReadablePromotionTitle(promotion)}</p>
        <div>Benefits: {getReadablePromotionValue(promotion)}</div>
      </div>
    );
  });
};

const OrderItemSelectOption = ({
  data,
  getStyles,
  innerProps,
  innerRef,
  ...props
}: OptionProps<OrderSelectLineItemV2>) => {
  const { order } = data as { order: generatedTypesNew.Order };
  const optionStyles = getStyles('option', props);

  return (
    <section ref={innerRef} {...innerProps} style={optionStyles} key={`select-option-${order.orderNumber}`}>
      <span data-testid={`select-option-${order.orderNumber}`}>
        <strong>{order?.orderNumber}</strong>
        <div>
          <FormattedDate date={order.created} showTime />
        </div>
        {order.orderLineItems &&
          order.orderLineItems.nodes.map((lineItem, index) => {
            const orderLineItem = ` 
            ${getOrderLineItemStatusAndType(lineItem?.status, lineItem?.type)}
            ${lineItem?.parentEntitlementId ? 'add-on ' : ''} 
            ${getDescriptor(lineItem)}
        `;

            const promotions = lineItem?.promotions;
            const hasPromotions = promotions?.length;

            return (
              <React.Fragment key={index}>
                <p>
                  {capitalize(lineItem?.orderTransitionType || '')}{' '}
                  {lineItem?.salesTransitionType !== 'NA' && lowerCase(lineItem?.salesTransitionType || '')}
                </p>
                <div>{orderLineItem}</div>
                {hasPromotions && getPromotionDetailsMessage(promotions)}
              </React.Fragment>
            );
          })}
      </span>
    </section>
  );
};

interface Props {
  fieldName?: string;
  fieldLabel?: React.ReactNode;
  isRequired?: boolean;
  orderLineItems?: Order[] | null;
  onChange?: (orderItem: OrderSelectLineItemV2) => void;
  isSearchable?: boolean | false;
}

interface Option {
  label: string;
  value: string;
  data: any;
}

export const SelectOrderFormFieldV2 = ({
  fieldName = 'order',
  fieldLabel = 'Select Order to cancel',
  isRequired = false,
  orderLineItems,
  onChange,
  isSearchable,
}: Props) => {
  const orderSelectItems = (orderLineItems ?? []).map((order) => {
    return {
      order,
      value: order.orderNumber,
    } as OrderSelectLineItemV2;
  });

  const onSelectInputChange = (option: Option, selectValue: string): boolean => {
    const pricingPlan = get(option, 'data.order.orderLineItems.nodes[0].pricingPlan', null);
    if (pricingPlan) {
      return pricingPlan.type.toLowerCase().indexOf(selectValue.toLowerCase()) > -1;
    }
    return false;
  };

  return (
    <div>
      <Field<ValueType<OrderSelectLineItemV2>, HTMLSelectElement>
        name={fieldName}
        label={fieldLabel}
        isRequired={isRequired}
      >
        {({ fieldProps }) => (
          <div data-testid="order-select-dropdown">
            <Select<OrderSelectLineItemV2>
              {...fieldProps}
              options={orderSelectItems}
              isDisabled={orderSelectItems.length === 0}
              components={{
                Option: OrderItemSelectOption,
                //@ts-ignore
                SingleValue: OrderItemSelectOption,
              }}
              isSearchable={isSearchable}
              onChange={(selectedItem) => {
                if (selectedItem && selectedItem.value) {
                  onChange && onChange(selectedItem);
                  fieldProps.onChange(selectedItem);
                }
              }}
              filterOption={onSelectInputChange}
            />
          </div>
        )}
      </Field>
    </div>
  );
};
